import { memo, useRef, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle, type SyntheticEvent } from "react";

import { OverlayPanel } from "primereact/overlaypanel";

import { Button } from "primereact/button";
import { Icon } from "..";
import { ColorPicker } from "pages/tip-tap-editor/components/panels/Colorpicker";
import { useAppDispatch, useAppSelector, addRecentColor } from "utils/store";

const MemoColorPicker = memo(ColorPicker);

/** Imperative handle exposed via ref — lets a caller open the picker from somewhere other than
 * the trigger button itself, e.g. a "Background colour" row that should be clickable as a whole
 * rather than only its small swatch. */
export type ColorPickerButtonHandle = {
  toggle: (event: SyntheticEvent) => void;
};

export const ColorPickerButton = forwardRef<ColorPickerButtonHandle, ColorPickerProps>(function ColorPickerButton(
  { currentColor, onChangeColor, onClearColor, className, showClearSwatch, defaultDisplayColor = "#000000" },
  forwardedRef
) {
  const op = useRef<OverlayPanel>(null);
  // PrimeReact's OverlayPanel.toggle(event, target) anchors/positions the popup off `target`,
  // falling back to event.currentTarget when target is omitted. A row wrapping this component
  // (see ColorPickerButtonHandle) calls toggle() with ITS OWN click event, whose currentTarget
  // is the entire wide row — not this small swatch — so the popup would anchor to the row
  // instead. That only visibly misbehaves when there's limited horizontal room to align against
  // that much wider anchor (e.g. a left-aligned table with little space to its left), which is
  // why this looked position-dependent rather than a plain functional break. Always passing this
  // button explicitly as the target keeps anchoring identical regardless of what triggered it.
  const buttonRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(forwardedRef, () => ({
    toggle: (event) => op.current?.toggle(event as any, buttonRef.current),
  }));

  const dispatch = useAppDispatch();
  const recentColors = useAppSelector((state) => state.templateBuilderSlice.recentColors);
  const lastValue = useRef<string | null>(null);

  // Recording "recent" on every onChange (or even on a short inactivity debounce/pointerup) means
  // every intermediate shade dragged through — or briefly paused on between separate hue/
  // saturation/hex adjustments — gets recorded, flooding the recent-colours list with shades the
  // user never actually settled on. The only point that reliably reflects the colour the user
  // actually chose is when they're done with the picker altogether, i.e. when it closes.
  const handleChangeColor = useCallback(
    (value: string) => {
      onChangeColor(value);
      lastValue.current = value;
    },
    [onChangeColor]
  );

  const commitLastValue = useCallback(() => {
    if (lastValue.current) dispatch(addRecentColor(lastValue.current));
    lastValue.current = null;
  }, [dispatch]);

  // Table row/column colour pickers live inside a TipTap BubbleMenu that unmounts the instant the
  // cell selection changes — which can happen before PrimeReact's own OverlayPanel gets a chance
  // to fire its onHide below, so that event alone would never arrive for table backgrounds even
  // though it works fine for the persistent-sidebar text/divider pickers. Committing on unmount
  // too covers that case without needing to commit any earlier (e.g. on pointerup), since dispatch
  // is safe to call after unmount.
  useEffect(() => () => commitLastValue(), [commitLastValue]);

  const color = useMemo(() => {
    return currentColor || defaultDisplayColor;
  }, [currentColor, defaultDisplayColor]);

  // Contrast for the icon against the button's own background — must be based on `color` (what's
  // actually painted on the button), not raw currentColor. Using currentColor here was the bug:
  // its "unset" fallback (return true → white icon) assumed the button itself would default to
  // black in that case, which held before defaultDisplayColor existed but silently broke for
  // white-background pickers (table backgrounds) — white icon on a white button, i.e. the swatch
  // visually disappearing entirely after clearing a table's background colour.
  const isDarkColor = useMemo(() => {
    const c = color.substring(1); // strip #
    const rgb = parseInt(c, 16); // convert rrggbb to decimal
    const r = (rgb >> 16) & 0xff; // extract red
    const g = (rgb >> 8) & 0xff; // extract green
    const b = (rgb >> 0) & 0xff; // extract blue

    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

    return luma < 100;
  }, [color]);

  return (
    <>
      <Button
        // PrimeReact's .d.ts types Button's ref as the component instance (a legacy class-based
        // typing), but its actual JS source has no imperative handle for it — the ref is
        // forwarded straight to the underlying <button> DOM node via ObjectUtils.combinedRefs.
        // The cast reflects runtime reality, not the (misleading) declared type.
        ref={buttonRef as any}
        className={className ?? "h-[50px] hover:opacity-90"}
        style={{ backgroundColor: color, borderColor: color }}
        rounded
        children={<Icon name="Palette" className={isDarkColor ? "text-white" : "text-black"} />}
        onClick={(e: SyntheticEvent) => {
          // Defensive: stops a click on the swatch from also bubbling into a wrapping row's own
          // onClick (see ColorPickerButtonHandle) that would call toggle() a second time on the
          // same click.
          e.stopPropagation();
          op.current?.toggle(e as any, buttonRef.current);
        }}
      ></Button>

      <OverlayPanel ref={op} onHide={commitLastValue}>
        {/* OverlayPanel's content renders via a portal, so it's elsewhere in the real DOM — but
            it's still a React descendant of whatever JSX nests this ColorPickerButton, and React's
            synthetic events bubble along that component tree, not the portaled DOM tree. Without
            this, clicking anywhere inside the picker (a swatch, the hex wheel, the input) bubbles
            straight up to a wrapping row's own onClick (see ColorPickerButtonHandle) and toggles
            the panel closed on every interaction instead of staying open until an actual
            outside click. */}
        <div onClick={(e) => e.stopPropagation()}>
          <MemoColorPicker
            color={color}
            // The hex picker/input above always work off the defaulted `color` (react-colorful
            // needs a real hex string, not ""), but the clear-swatch's active state needs to know
            // whether a colour is genuinely set rather than defaulted — hence this separate flag
            // off the raw currentColor instead of reusing `color`.
            isColorUnset={!currentColor}
            onChange={handleChangeColor}
            onClear={onClearColor}
            recentColors={recentColors}
            showClearSwatch={showClearSwatch}
          />
        </div>
      </OverlayPanel>
    </>
  );
});

interface ColorPickerProps {
  currentColor: string | null | undefined;
  onChangeColor: (value: string) => void;
  onClearColor: () => void;
  className?: string;
  /** Shows a permanent "no colour" swatch — see Colorpicker.tsx's showClearSwatch for when this
   * is appropriate (background colour contexts only, not text/line colour). */
  showClearSwatch?: boolean;
  /** Trigger button's own fallback swatch colour when currentColor is unset — e.g. white for a
   * background-colour picker, where the default black text-colour fallback would look wrong.
   * Only affects the trigger button/hex-picker display; currentColor itself stays raw so
   * showClearSwatch's active state can tell "unset" apart from "explicitly this colour". */
  defaultDisplayColor?: string;
}

ColorPickerButton.displayName = "SidebarMenu.ColorPicker";
